import { memo, useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import type { HotelTemplateType } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog/dialog";
import { Image } from "@/components/ui/image/image";

import { useUpdateHotelTemplateSettings } from "../../../api";

export type HotelTemplateListItemProps = {
	templateName: string;
	hotelTemplateType: HotelTemplateType;
	description: string;
	keyVisualUrl: string;
	previewUrl: string;
};

type Props = {
	hotelID: string;
} & HotelTemplateListItemProps;

export const HotelTemplateListItem = memo(
	({ hotelID, templateName, hotelTemplateType, description, keyVisualUrl, previewUrl }: Props) => {
		const handleClickPreview = useCallback(() => {
			window.open(previewUrl, "_blank");
		}, [previewUrl]);

		const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
		const navigate = useNavigate();

		const navigateToHotelTop = useCallback(() => {
			navigate(generatePath(PATHS.HOTEL.TOP, { hotelID }), { replace: true });
		}, [hotelID, navigate]);

		const { updateHotelTemplateSettings } = useUpdateHotelTemplateSettings();

		const handleClickStart = useCallback(async () => {
			showLoadingScreenCallback();

			const { data, errors } = await updateHotelTemplateSettings({
				hotelID,
				hotelTemplateType,
			}).finally(hideLoadingScreenCallback);

			if (!data || errors) {
				notifyUserEventInfo({
					variant: "error",
					title: "テンプレートの選択ができませんでした",
					description: "時間を置いて再度お試しください",
				});

				return;
			}

			notifyUserEventInfo({
				title: "選択したテンプレートを適用しました",
				description: "スタートガイドに沿って設定を進めましょう！",
			});
			navigateToHotelTop();
		}, [
			hideLoadingScreenCallback,
			hotelID,
			hotelTemplateType,
			navigateToHotelTop,
			showLoadingScreenCallback,
			updateHotelTemplateSettings,
		]);

		return (
			<div className="rounded-xl border-2 border-zinc-300 p-5 md:px-8 md:pb-8 md:pt-6">
				<div className="space-y-8">
					<div className="space-y-4">
						<p className="text-xl font-semibold">{templateName}</p>

						<div className="flex h-12 items-center gap-x-2">
							<p className="line-clamp-3 self-start whitespace-pre-line">{description}</p>
						</div>
					</div>

					<div className="flex items-center justify-center">
						<Image src={keyVisualUrl} alt={templateName} />
					</div>

					<div className="flex flex-col gap-y-2 md:flex-row md:gap-x-3">
						<Button variant="outline" className="w-full md:w-1/2" onClick={handleClickPreview}>
							プレビュー
						</Button>

						<Dialog>
							<DialogTrigger asChild>
								<Button variant="default" className="w-full md:w-1/2">
									この設定ではじめる
								</Button>
							</DialogTrigger>

							<DialogContent>
								<div className="space-y-10 p-2 lg:p-6">
									<div className="space-y-6">
										<DialogHeader>
											<DialogTitle>テンプレート機能利用時の注意</DialogTitle>
										</DialogHeader>

										<p>
											商品の名前や価格、在庫数などはすべて仮の値が入力されています。任意の設定に変更した上で販売を開始してください。
										</p>
									</div>

									<div className="flex justify-center">
										<Button variant="default" size="xl" onClick={handleClickStart}>
											設定をはじめる
										</Button>
									</div>
								</div>
							</DialogContent>
						</Dialog>
					</div>
				</div>
			</div>
		);
	},
);
